/**
 * AI 생성기 — 빌드 화면 아래에 떠 있는 입력창. 순수 UI 다.
 *
 * 입력창이 열렸을 때(open)만 입력·자료·조건 전체를 그린다. 닫혀 있으면 아무것도 그리지 않는다.
 * - 생성 중: 그리지 않는다 — 진행은 생성 중 토스트(useAiGenerateDockRunningToast)와 목록 끝 대기 카드가 보여 준다.
 * - 20문제 가득: 입력창을 열지 않고 상한 토스트를 띄운다(useRequestAiGenerateDock).
 */
import { type CSSProperties, type Ref, useState } from 'react';
import { TextArea } from '@zep-us/zus';
import { IconButton } from '@zep-us/zus/button';
import { Gray50, ICON_COLOR } from '@zep-us/zus/tokens';
import { Tooltip } from '@zep-us/zus/tooltip';
import { ArrowUpIcon, XSmallIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { AI_GENERATE_DOCK_FIXED_INSET_CLASS, AI_GENERATE_DOCK_STATIC_INSET_CLASS } from '../consts/aiGenerateDockInset.const';
import { type AiGenerateDockOnboardingStep } from '../consts/aiGenerateDockOnboarding.const';
import { type AiGenerateDockAttachment, type AiGenerateDockConditions, type AiGenerateDockCopy, type AiGenerateDockNoticeData, type AiGenerateDockOnboardingCopy } from '../types/aiGenerateDock.types';
import { resolveAiGenerateDockCount } from '../utils/aiGenerateDockCount';
import { resolveAiGenerateDockSourceRow } from '../utils/aiGenerateDockSourceRow';
import { AiGenerateDockAttachFileButton } from './AiGenerateDockAttachFileButton';
import { AiGenerateDockAttachmentChip } from './AiGenerateDockAttachmentChip';
import { AiGenerateDockConditionsField } from './AiGenerateDockConditionsField';
import { type ConditionLevel } from './AiGenerateDockConditionsMenu';
import { AiGenerateDockLinkInput } from './AiGenerateDockLinkInput';
import { AiGenerateDockNotice } from './AiGenerateDockNotice';
import { AiGenerateDockOnboardingUI } from './AiGenerateDockOnboardingUI';
import { AiGenerateDockSourceRow } from './AiGenerateDockSourceRow';
import { AiGenerateDockSourceSwitch } from './AiGenerateDockSourceSwitch';
import { AiGenerateDockTopicInput } from './AiGenerateDockTopicInput';
export type AiGenerateDockUIProps = {
  copy: AiGenerateDockCopy;
  /** fixed: 화면 아래 고정(실사용) · static: 스토리북·테스트처럼 흐름 안에 둔다. */
  placement?: 'fixed' | 'static';
  /** 좁은 화면(tablet-sm 미만)이면 narrow — 조건을 「문제 설정」 시트로 연다. 컨테이너가 정한다. */
  layout?: 'wide' | 'narrow';
  /** 입력창 루트 ref — 컨테이너가 모바일 키보드 보정을 건다. */
  rootRef?: Ref<HTMLDivElement>;
  onClose: () => void;
  sourceType: AiGenerateDockSourceType;
  onSourceTypeChange: (sourceType: AiGenerateDockSourceType) => void;
  /**
   * 입력칸 값. 주제 탭은 주제(필수), 자료 탭은 자료에서 다룰 범위(선택)다.
   * 어느 탭이든 서버에는 `direct_generate.topic` 으로 간다.
   */
  topic: string;
  onTopicChange: (topic: string) => void;
  linkUrl: string;
  onLinkUrlChange: (url: string) => void;
  onLinkSubmit: () => void;
  attachment: AiGenerateDockAttachment | null;
  onAttachFile: () => void;
  onRemoveAttachment: () => void;
  conditions: AiGenerateDockConditions;
  onConditionsChange: (conditions: AiGenerateDockConditions) => void;
  /** 20 - 지금 문제 수. 0 이면 잠긴다. */
  remainingSlots: number;

  /** 입력창이 보이나(aiGenerateDockInputShownAtom) — 아니면 그리지 않는다. */
  open: boolean;
  onSubmit: () => void;
  /** 지금 실행할 수 없는 이유(예: AI 채팅이 답하는 중). 있으면 버튼을 잠그고 툴팁에 이유를 보인다. */
  submitBlockedReason?: string;
  /** 무료 남은 횟수 등 — 실행 버튼 hover 툴팁에 「문제 만들기」와 함께 보인다(PRD 5-4). */
  usageHint?: string;
  notice?: AiGenerateDockNoticeData | null;
  /** 조건 메뉴·시트를 펼친 채 시작한다 — 스토리북 시나리오용. */
  defaultConditionsOpen?: boolean;
  /**
   * 입력창이 화면에 보이는가(기본 true). 감싼 래퍼가 숨겨져도 조건 메뉴·시트는 body 포털에 그려져 남으므로,
   * false 가 되면 펼쳐 둔 조건을 닫는다.
   */
  visible?: boolean;
  /** 펼친 조건에서 먼저 보일 단계 — 스토리북 시나리오용. */
  defaultConditionLevel?: ConditionLevel | null;
  /** 조건 메뉴·시트를 열거나 닫을 때 — 코치마크가 「조건을 열었다」를 알아 걷는다. */
  onConditionsOpenChange?: (open: boolean) => void;
  /** 처음 열었을 때 코치마크 — 있으면 그 단계의 대상(입력칸·자료 탭·조건)을 강조한다. 무엇을 보일지는 컨테이너가 정한다. */
  onboarding?: AiGenerateDockOnboardingView | null;
};
export type AiGenerateDockOnboardingView = {
  step: AiGenerateDockOnboardingStep;
  copy: AiGenerateDockOnboardingCopy;
  onNext: () => void;
  onEscape: () => void;
};

/**
 * 입력창 껍데기 — 빌드 카드보다 한 단계 떠 있는 흰 상자(둥근 24px, 1px border/opacity, shadow-xl).
 * 그림자는 Figma shadow/md 보다 두 단계 크게 — 흐린 화면 위에서 입력창이 떠 보이게(10/23 지민 「더 큰 그림자여야 할 듯」, md·lg·xl 비교 후 xl).
 * 외곽선은 안쪽 ring 으로 그린다 — Figma 선처럼 크기를 차지하지
 * 않아 안쪽 여백이 Figma 값 그대로 맞고, 입력 중 ZUS 포커스 링(안쪽 2px)도 같은 자리에 그려진다.
 */
const SHELL_CLASS = 'relative w-full rounded-lg bg-default shadow-xl ring-1 ring-inset ring-opacity-black-3';

/**
 * ZUS TextArea.InputWrapper 를 입력창 껍데기로 쓴다 — 모양(모서리 24·1px border/opacity·shadow-xl)만 바꾸고
 * 입력 중 외곽선은 ZUS 기본(안쪽 2px ring-strong-default = Figma input/border/focused #A49BFF)을 그대로 쓴다.
 * 안쪽 여백은 영역(위 · 도구 줄)이 각자 갖는다(Figma .AIGen/PromptInput).
 */
const TEXT_SHELL_CLASS = cn(SHELL_CLASS, 'flex-col items-stretch border-0 p-0');

/** M4 입력창 열기 — fade + 8px 위로 250ms ease-out. 뒤 흐림도 같은 타이밍. 모션 줄이기면 끈다. */
const OPEN_MOTION_CLASS = 'animate-in fade-in slide-in-from-bottom-2 [animation-duration:250ms] ease-out motion-reduce:animate-none';

/**
 * 뒤 흐림 — Figma 2-1 「뒤 흐림」(1440×840, 헤더 아래) = gray-50 채움(위 0 → 가운데 0.25 → 아래 0.6) + 진행형 배경 흐림
 * (BACKGROUND_BLUR PROGRESSIVE, 반경 10 = CSS blur 5px, 위 0% 에서 0 → 아래 90% 에서 꽉 참, 그 아래는 유지).
 * 그 렌더를 높이별로 재면 흐림이 직선으로 커진다(25% 1.25px · 50% 2.5px · 65% 3px).
 *
 * CSS 로는 흐림 층을 띠마다 **쌓는다** — 각 층은 자기 띠에서 나타나 그 아래로는 계속 덮는다. 층마다 나타났다 사라지게
 * 엇갈리면 두 층이 반씩 겹치는 곳에서 흐리지 않은 원본이 비쳐 글자에 선명한 심이 남는다(10/23 지민 — 「문제 2 글자만 봐도
 * 안 예뻐」). 쌓인 층은 아래 층 결과를 다시 흐리므로 세기가 제곱합으로 더해진다 — i 번째 층을 √(i²−(i−1)²) 배로 줘
 * 누적이 직선이 되게 한다.
 *
 * 층 수·면적이 곧 GPU 비용이다 — 쌓은 층은 차례로 계산되고, 뒤 화면이 바뀔 때마다(진입 카드 로고 롤링 등) 다시 흐려진다.
 * 그래서 층은 3장이고, 각 층은 자기 띠 위쪽까지만 차지한다(그 위는 mask 로 투명). 위쪽 가장자리는 블러 커널(3σ)만큼
 * 더 올려, mask 가 보이는 곳은 화면 전체를 덮을 때와 같은 뒤 화면을 읽게 한다.
 *
 * 띠는 화면 높이의 % 가 아니라 **화면 아래(입력창)에서 잰 px** 로 건다(시안 840 기준). % 로 걸면 낮은 화면에서 흐림 구간이
 * 위로 눌려 맨 위 카드까지 흐려진다. 아래 기준이면 어떤 높이에서도 입력창 위 모양이 시안과 같다.
 */
const BLUR_DESIGN_HEIGHT_PX = 840;
const BLUR_LAYER_COUNT = 3;
const BLUR_MAX_PX = 5;
/** 시안에서 흐림이 꽉 차는 지점(위에서 %). */
const BLUR_FULL_AT_PERCENT = 90;
/** CSS blur(Npx) 는 σ=N 인 가우시안 — 3σ 밖은 결과에 영향이 없다. 층 위쪽 가장자리를 이만큼 더 올린다. */
const BLUR_KERNEL_SIGMAS = 3;
const PROGRESSIVE_BLUR_LAYERS = Array.from({
  length: BLUR_LAYER_COUNT
}, (_, index) => {
  const step = BLUR_FULL_AT_PERCENT / BLUR_LAYER_COUNT;
  const unit = BLUR_MAX_PX / BLUR_LAYER_COUNT;
  return {
    blurPx: Number((unit * Math.sqrt(2 * index + 1)).toFixed(2)),
    startPercent: step * index,
    fullPercent: step * (index + 1)
  };
});

/** 시안 위에서 잰 % → 화면 아래에서 잰 px. */
const fromBottomPx = (percentFromTop: number) => `${Math.round(BLUR_DESIGN_HEIGHT_PX * (100 - percentFromTop) / 100)}px`;
const toBlurLayerStyle = ({
  blurPx,
  startPercent,
  fullPercent
}: (typeof PROGRESSIVE_BLUR_LAYERS)[number]): CSSProperties => {
  // to top — 아래(0px)부터 띠 끝까지 꽉 차 있고, 띠 시작으로 올라가며 사라진다.
  const mask = `linear-gradient(to top, black ${fromBottomPx(fullPercent)}, transparent ${fromBottomPx(startPercent)})`;
  return {
    height: `min(100%, calc(${fromBottomPx(startPercent)} + ${Math.ceil(BLUR_KERNEL_SIGMAS * blurPx)}px))`,
    backdropFilter: `blur(${blurPx}px)`,
    WebkitBackdropFilter: `blur(${blurPx}px)`,
    maskImage: mask,
    WebkitMaskImage: mask
  };
};

/** gray-50 채움도 아래 기준 — 아래 0.6(99) → 420px 위 0.25(40) → 840px 위 0(00). 16진 알파를 토큰 뒤에 붙인다. */
const BACKDROP_FILL_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(to top, ${Gray50}99 0px, ${Gray50}40 ${fromBottomPx(50)}, ${Gray50}00 ${fromBottomPx(0)})`
};

/**
 * 실행할 거리가 있나 — 붙은 자료가 있으면 그 자료로, 없으면 어느 탭이든 입력칸 글(주제)로 만든다.
 * 자료를 올리거나 가져오는 중이면 끝날 때까지 기다린다.
 */
const hasRequiredInput = (topic: string, attachment: AiGenerateDockAttachment | null) => attachment ? attachment.status === 'ready' : topic.trim().length > 0;

/**
 * 실행 버튼 — 둥근 ↑ 아이콘 버튼(PC·모바일 같은 모양). 이름은 aria-label, hover 툴팁에
 * 「문제 만들기」와 남은 횟수·잠긴 이유를 보인다. 꺼진 버튼도 툴팁이 뜨도록 트리거를 감싼다.
 */
const SubmitButton = ({
  label,
  tooltip,
  disabled,
  onClick,
  testId
}: {
  label: string;
  tooltip: string;
  disabled: boolean;
  onClick?: () => void;
  testId?: string;
}) => <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="SubmitButton" data-sentry-source-file="AiGenerateDockUI.tsx">
    <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockUI.tsx">
      <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockUI.tsx">
        <span className="inline-flex shrink-0" tabIndex={disabled ? 0 : -1}>
          <IconButton size="md" variant="contained" intent="strong" rounded="full" aria-label={label} data-testid={testId} disabled={disabled} onClick={onClick} data-sentry-element="IconButton" data-sentry-source-file="AiGenerateDockUI.tsx">
            <ArrowUpIcon size="sm" fill={ICON_COLOR.inverse} aria-hidden data-sentry-element="ArrowUpIcon" data-sentry-source-file="AiGenerateDockUI.tsx" />
          </IconButton>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content side="top" sideOffset={2} arrow data-testid="ai-generate-dock-submit-tooltip" data-sentry-element="unknown" data-sentry-source-file="AiGenerateDockUI.tsx">
        {tooltip}
      </Tooltip.Content>
    </Tooltip.Root>
  </Tooltip.Provider>;
export const AiGenerateDockUI = (props: AiGenerateDockUIProps) => {
  const {
    copy,
    placement = 'fixed',
    layout = 'wide',
    rootRef,
    onClose,
    conditions,
    remainingSlots,
    open,
    onSubmit,
    submitBlockedReason,
    usageHint,
    notice,
    sourceType,
    topic,
    attachment
  } = props;
  const [conditionsOpen, setConditionsOpenState] = useState(props.defaultConditionsOpen ?? false);
  const setConditionsOpen = (next: boolean) => {
    setConditionsOpenState(next);
    props.onConditionsOpenChange?.(next);
  };
  // 코치마크가 강조할 곳 — 그려진 뒤에야 위치를 잴 수 있어 상태로 받는다.
  const [onboardingTargets, setOnboardingTargets] = useState<Partial<Record<AiGenerateDockOnboardingStep, HTMLElement | null>>>({});
  // ref 함수는 한 번만 만든다 — 렌더마다 새 함수면 React 가 null → 요소를 다시 넘겨 상태가 계속 바뀐다.
  const [onboardingTargetRefs] = useState(() => {
    const refOf = (step: AiGenerateDockOnboardingStep) => (node: HTMLElement | null) => setOnboardingTargets(prev => prev[step] === node ? prev : {
      ...prev,
      [step]: node
    });
    return {
      input: refOf('input'),
      source: refOf('source'),
      conditions: refOf('conditions')
    } satisfies Record<AiGenerateDockOnboardingStep, Ref<HTMLDivElement>>;
  });
  // 숨겨지면 조건 메뉴·시트를 닫는다 — 다시 보일 때 펼친 채로 돌아오지 않게 상태 자체를 닫는다.
  const visible = props.visible ?? true;
  const [prevVisible, setPrevVisible] = useState(visible);
  if (prevVisible !== visible) {
    setPrevVisible(visible);
    if (!visible) setConditionsOpenState(false);
  }
  const countState = resolveAiGenerateDockCount({
    remaining: remainingSlots,
    selected: conditions.count
  });
  const canSubmit = !countState.locked && !submitBlockedReason && hasRequiredInput(topic, attachment);
  const submit = () => {
    if (canSubmit) onSubmit();
  };
  const submitTooltip = submitBlockedReason ?? [copy.submit, usageHint].filter(Boolean).join(' · ');

  // 남은 칸이 없으면 입력창을 열지 않으므로(useRequestAiGenerateDock) 여기서도 그리지 않는다.
  if (!open || countState.locked) return null;
  const body = <TextArea.Root required={false} value={topic}>
      <TextArea.InputWrapper data-testid="ai-generate-dock-expanded" className={TEXT_SHELL_CLASS}>
        {/* Figma top — 위 12 · 왼쪽 16 · 오른쪽 12. 최소 116(입력창 최소 176) — 시안 94 에서 글 한 줄(22)을 더 키웠다(10/23 지민).
              글 영역(위 4, 자료 줄과 12)과 닫기 사이 8. */}
        <div ref={onboardingTargetRefs.input} className="flex min-h-[116px] items-start gap-xs pl-md pr-sm pt-sm">
          <div className="flex min-w-0 flex-1 flex-col gap-sm pt-3xs">
            <AiGenerateDockSourceRow row={resolveAiGenerateDockSourceRow(sourceType, attachment)} attachment={attached => <AiGenerateDockAttachmentChip attachment={attached} copy={copy} onRemove={props.onRemoveAttachment} />} file={() => <AiGenerateDockAttachFileButton copy={copy} onAttach={props.onAttachFile} />} link={linkSourceType => <AiGenerateDockLinkInput sourceType={linkSourceType} copy={copy} value={props.linkUrl} onChange={props.onLinkUrlChange} onSubmit={props.onLinkSubmit} />} />
            <AiGenerateDockTopicInput placeholder={sourceType === 'query' ? copy.topicPlaceholder : copy.scopePlaceholder} value={topic} onChange={props.onTopicChange}
          // 코치마크가 떠 있는 동안 Enter 로 실행하지 않는다 — 실행하면 입력창이 닫혀 안내가 중간에 끊긴다.
          // 실행 버튼은 dim 이 이미 막는다.
          onSubmitShortcut={props.onboarding ? undefined : submit}
          // 코치마크가 사라지면(「시작하기」·Esc) 바로 쓸 수 있게 입력칸으로 포커스를 돌린다.
          focusKey={`${sourceType}:${props.onboarding ? 'onboarding' : 'ready'}`} />
          </div>
          <IconButton size="xs" variant="transparent" intent="neutral" aria-label={copy.close} data-testid="ai-generate-dock-close" className="shrink-0" onClick={onClose}>
            <XSmallIcon size="sm" fill={ICON_COLOR.neutral} />
          </IconButton>
        </div>
        {/* toolbar — 위 8 · 아래 16 · 왼쪽 16 · 오른쪽 12(탭 판이 입력창 모서리에 붙어 보이지 않게, 10/23 지민).
              탭과 조건 칩 사이 4. */}
        <div className="flex min-w-0 items-center gap-3xs pb-md pl-md pr-sm pt-xs">
          <div ref={onboardingTargetRefs.source} className="flex shrink-0">
            <AiGenerateDockSourceSwitch copy={copy} sourceType={sourceType} onSourceTypeChange={props.onSourceTypeChange} />
          </div>
          <div ref={onboardingTargetRefs.conditions} className="flex min-w-0">
            <AiGenerateDockConditionsField copy={copy} conditions={conditions} onConditionsChange={props.onConditionsChange} countState={countState} remainingSlots={remainingSlots} open={conditionsOpen} onOpenChange={setConditionsOpen} presentation={layout === 'narrow' ? 'sheet' : 'menu'} defaultLevel={props.defaultConditionLevel} />
          </div>
          <span className="flex-1" />
          <SubmitButton label={copy.submit} tooltip={submitTooltip} testId="ai-generate-dock-submit" disabled={!canSubmit} onClick={submit} />
        </div>
      </TextArea.InputWrapper>
    </TextArea.Root>;
  const isFixed = placement === 'fixed';
  const backdropLayerClass = cn(isFixed ? 'fixed' : 'absolute', 'inset-0 z-30', OPEN_MOTION_CLASS);
  // 흐림 층은 아래에 붙어 자기 높이(toBlurLayerStyle)만 차지한다.
  const blurLayerClass = cn(isFixed ? 'fixed' : 'absolute', 'inset-x-0 bottom-0 z-30', OPEN_MOTION_CLASS);
  return <>
      {
    // 입력창이 열린 동안 뒤 화면을 흐린다(PRD 3-1 · M4). 흐린 배경은 뒤 화면 클릭을 막기만 하고 누르도
    // 닫지 않는다 — 쓰던 입력을 실수로 잃지 않게. 닫기는 ✕ 와 Esc 로만 한다.
    // Figma 「뒤 흐림」 — gray-50 채움 위 0 → 가운데 25% → 아래 60% 위에 점점 세지는 흐림(PROGRESSIVE_BLUR_LAYERS).
    // 흐림 층은 채움 층의 자식이면 부모 페이드(opacity) 동안 뒤 화면을 못 보므로 형제로 둔다.
    <>
          {PROGRESSIVE_BLUR_LAYERS.map(layer => <div key={layer.startPercent} aria-hidden className={blurLayerClass} style={toBlurLayerStyle(layer)} />)}
          <div aria-hidden data-testid="ai-generate-dock-backdrop" className={backdropLayerClass} style={BACKDROP_FILL_STYLE} />
        </>}
      <div ref={rootRef} data-testid="ai-generate-dock" onKeyDown={event => {
      // 조건 메뉴·시트·툴팁이 먼저 Esc 를 처리했으면(Radix 가 document capture 에서 preventDefault 하고 닫는다)
      // 입력창은 닫지 않는다. 메뉴가 먼저 닫히며 conditionsOpen 이 이미 false 가 된 뒤라 그 값만으로는 못 막는다.
      if (event.key === 'Escape' && !event.defaultPrevented && !conditionsOpen) {
        event.stopPropagation();
        onClose();
      }
    }} className={cn(isFixed ? AI_GENERATE_DOCK_FIXED_INSET_CLASS : AI_GENERATE_DOCK_STATIC_INSET_CLASS, 'pointer-events-none z-40 flex w-full justify-center px-md')}>
        <div className={cn('pointer-events-auto flex w-full max-w-[650px] flex-col gap-xs', OPEN_MOTION_CLASS)}>
          {notice && <AiGenerateDockNotice notice={notice} />}
          <div className="relative flex flex-col">
            {/* Figma 「AI 글로우」 — 입력창 위 끝에서 시작하는 770×198 타원(좌우 60 넘침), 보라→파랑 가로 그라디언트, blur 40. */}
            <div aria-hidden data-testid="ai-generate-dock-glow" className="absolute left-1/2 top-0 -z-10 h-[198px] w-[calc(100%+120px)] -translate-x-1/2 rounded-[50%] bg-[linear-gradient(to_right,rgb(158_140_255/0.16),rgb(115_158_255/0.14))] blur-[40px]" />
            {body}
          </div>
        </div>
      </div>
      {props.onboarding && <AiGenerateDockOnboardingUI step={props.onboarding.step} target={onboardingTargets[props.onboarding.step] ?? null} copy={props.onboarding.copy} onNext={props.onboarding.onNext} onEscape={props.onboarding.onEscape} />}
    </>;
};